import { Dispatch, useContext, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { Fonts } from '../../../../config/theme';
import styled from 'styled-components';
import {
  CardStyled,
  HeadingStyled,
  ThemeModal,
} from '../../../../styles/style';
import { LISTING_TYPES } from '../../../../config/constants';
import {
  AttributeAssociation,
  ListingState,
} from '../../../../data/types/listings';
import { ProductListingContext } from '../../../../pages/create_listings/functions/data_formatting_functions';
import { BaseSelectAttribute } from '../../../../data/types/attributes';
import { ImageType } from '../../../../data/types/files';
import ShowImage from '../../../../components/show_image';
import deviceFrame_path from '../../../../assets/images/placeholders/device-mock.png';
import { Button } from '@nextui-org/react';

type PreviewProps = {
  remove: () => void;
  requestPublish: (type: number) => void;
};
export default function Preview({ remove, requestPublish }: PreviewProps) {
  const [confirmModal, setConfirmModal] = useState(false);
  const { state }: { state: ListingState; dispatch: Dispatch<any> } =
    useContext(ProductListingContext);
  const handleClose = () => {
    setConfirmModal(false);
  };
  const handleOpen = () => {
    setConfirmModal(true);
  };

  const handleConfirm = () => {
    requestPublish(LISTING_TYPES.public_listing);
    handleClose();
  };

  // Header Component
  const Header: React.FC = () => (
    <div className="header">
      <div className="back-arrow">&#8592;</div>
      <div className="icons"></div>
    </div>
  );

  // Image Carousel Component
  type ImageCarouselProps = {
    images: ImageType[];
  };

  const ImageCarousel: React.FC<ImageCarouselProps> = ({ images }) => (
    <div className="image-carousel">
      {images.map((image) => (
        <ShowImage
          key={image?.id}
          image={image}
          style={{ height: '300px', width: '100%' }}
        />
      ))}
      <div className="dots">
        {images.map((_, idx) => (
          <span key={idx} className="dot"></span>
        ))}
      </div>
    </div>
  );

  // Product Details Component
  type ProductDetailsProps = {
    name: string;
    price: string;
    category: string;
    description: string;
  };

  const ProductDetails: React.FC<ProductDetailsProps> = ({
    name,
    price,
    category,
  }) => (
    <div className="product-details">
      <h2>{name}</h2>
      <div className="price-category">
        <span>from {price} ILS</span>
        <span>{category}</span>
      </div>
      <div>No reviews</div>
    </div>
  );

  // Variants Component for selecting options like Color, Storage, Cellular, etc.
  type VariantsProps = {
    attributes: BaseSelectAttribute[];
  };

  const Variants: React.FC<VariantsProps> = ({ attributes }) => (
    <div className="variants-section">
      {attributes.map((attribute) => (
        <div key={attribute?.id} className="variant">
          <span>{attribute.name_en}:</span>
          <div className="variant-options">
            {attribute.values_en.map((value: string) => (
              <button key={value} className="variant-option">
                {value}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Description Component
  type DescriptionProps = {
    description: string;
  };

  const Description: React.FC<DescriptionProps> = ({ description }) => (
    <div className="description">
      <h3>Description</h3>
      <p>{description}</p>
    </div>
  );

  // Table for Specifications
  type SpecificationsTableProps = {
    attributes: Record<number, AttributeAssociation>;
  };

  const SpecificationsTable: React.FC<SpecificationsTableProps> = ({
    attributes,
  }) => (
    <div className="specs-table">
      <h3>Table of Specifications</h3>
      <table>
        <thead>
          <tr>
            <th>Attribute Name</th>
            <th>Attribute Value</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(attributes).map((key) => (
            <tr key={key}>
              <td>{attributes[Number(key)].attr_name_en}</td>
              <td>{attributes[Number(key)].official_values_en?.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Delivery Information and Qualities Component
  type DeliveryInfoProps = {
    data: {
      whats_included: string;
      weight: string;
      width: string;
      height: string;
      length: string;
      fragile: boolean;
      flammable: boolean;
      keep_dry: boolean;
    };
  };

  const DeliveryInformation: React.FC<DeliveryInfoProps> = ({ data }) => (
    <div className="delivery-information">
      <h3>Delivery Information</h3>
      <ul>
        <li>
          What's included: <b>{data.whats_included}</b>
        </li>
        <li>
          Dimensions: {`${data.length}cm - ${data.width}cm - ${data.height}cm`}
        </li>
        <li>Weight: {data.weight}g</li>
        <li>
          Item qualities:
          <div className="qualities">
            {data.fragile && <span>Fragile</span>}
            {data.keep_dry && <span>Keep Dry</span>}
          </div>
        </li>
      </ul>
    </div>
  );

  // Main Product Preview Component
  const MobilePreview = ({ ar = false }: { ar?: boolean }) => {
    console.log('ar', ar);
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'scroll',
        }}
      >
        <Header />
        <ImageCarousel images={state.data.imagesList} />
        <ProductDetails
          name={state.data.name_en}
          price={state.data.price}
          category={state.data.category?.name_en ?? ''}
          description={state.data.description_en}
        />
        <Variants attributes={state.data.select_attrs} />
        <Description description={state.data.description_en} />
        <SpecificationsTable
          attributes={state.data.official_attributes || {}}
        />
        <DeliveryInformation data={state.data} />
      </div>
    );
  };

  const PreviewWrapper = ({ ar = false }: { ar?: boolean }) => {
    console.log('ar', ar);
    return (
      <div
        style={{
          position: 'relative',
          maxWidth: '400px',
        }}
      >
        {/* The Image for the Device Frame */}
        <img
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%', // Adjust width as needed
            height: 'auto', // Maintain aspect ratio
          }}
          src={deviceFrame_path}
          alt="Device Frame"
        />
        {/* The Mobile Preview Content */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '32px',
            width: '90%',
            height: '95%',
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          <MobilePreview />
          <MobilePreview ar />
        </div>
      </div>
    );
  };

  return (
    <Wrapper>
      <Row>
        <Col
          lg={8}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            backgroundColor: '#fff',
            borderRadius: '5px',
          }}
        >
          <h5
            style={{
              fontWeight: 700,
              margin: '20px 10px',
            }}
          >
            Preview
          </h5>
          <h5
            style={{
              fontWeight: 400,
              margin: '20px 10px',
              color: '#4C6870',
            }}
          >
            This feature is in development
          </h5>
          {/* <PreviewWrapper />
          <PreviewWrapper ar={true} /> */}
        </Col>
        <Col lg={4}>
          <CardStyled>
            <div className="end">
              <Button onClick={remove} className="mx-3" style={{}}>
                Go Back
              </Button>
              <Button color="success" onClick={handleOpen}>
                Publish
              </Button>
            </div>
          </CardStyled>
        </Col>
      </Row>

      <Modal
        show={confirmModal}
        onHide={handleClose}
        centered
        className="modal-width-420 modal-title-center"
      >
        <ThemeModal>
          <Modal.Header closeButton />
          <Modal.Title>Submit item listing?</Modal.Title>

          <Modal.Body className="mb-2">
            <HeadingStyled
              className="text-center secondary-font"
              style={{
                fontSize: '15px',
                color: '#4C6870',
                fontWeight: '500',
                lineHeight: '22px',
                margin: '0 0 15px 0',
              }}
            >
              After submitting the listing, Silal QA team will review it and
              update accordingly if anything needed.
            </HeadingStyled>

            <div className="d-flex justify-content-between mt-1">
              <Button
                className="m-1 back"
                style={{
                  width: '50%',
                  background: 'white',
                  color: '#05AE4B',
                }}
                onClick={() => {
                  requestPublish(LISTING_TYPES.draft);
                  handleClose();
                }}
              >
                Save as draft
              </Button>
              <Button
                className="m-1"
                color="success"
                onClick={handleConfirm}
                style={{
                  width: '50%',
                }}
              >
                Submit
              </Button>
            </div>
          </Modal.Body>
        </ThemeModal>
      </Modal>
    </Wrapper>
  );
}
const Wrapper = styled.div`
  background-color: #f1f2f3;

  .back {
    border: 1px solid #05ae4b;
    border-radius: 5px;
  }
  .modal-header {
    padding: 10px;
  }
  .modal-body {
    padding: 20px;
  }
  .pincode-input-container {
    .pincode-input-text {
      padding: 0 !important;
      margin: 0 5px !important;
      width: 45px !important;
      height: 45px !important;
      border-radius: 5px;
      background: rgba(241, 242, 243, 0.5) !important;
      font-family: ${Fonts.primary};
      font-weight: 500;
      font-size: 25px;
      line-height: 22px;
      color: #4c6870;
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    font-size: 24px;
    padding: 8px;
  }

  .back-arrow {
    cursor: pointer;
  }

  .image-carousel {
    text-align: center;
  }

  .image-carousel img {
    width: 100%;
    border-radius: 12px;
  }

  .dots {
    display: flex;
    justify-content: center;
    margin-top: 8px;
  }

  .dot {
    height: 8px;
    width: 8px;
    background-color: grey;
    border-radius: 50%;
    margin: 0 4px;
  }

  .product-details {
    padding: 16px;
  }

  .price-category {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .variants-section {
    padding: 16px 0;
  }

  .variant {
    margin-bottom: 8px;
    font-size: 16px;
  }

  .variant-options {
    display: flex;
  }

  .variant-option {
    padding: 8px;
    margin-right: 5px;
    border: 1px solid #dedede;
    border-radius: 5px;
    cursor: pointer;
    background-color: white;
  }

  .variant-option:hover {
    background-color: lightgrey;
  }

  .description {
    padding: 16px 0;
  }

  .specs-table {
    margin-top: 16px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 8px;
    border: 1px solid #dedede;
  }

  .delivery-information {
    padding: 16px 0;
  }

  .qualities {
    display: flex;
    gap: 10px;
    padding-top: 8px;
  }
`;
